import React, { useEffect, useCallback, useState, useMemo } from 'react';
import IndicatorDetailsIntro from './IndicatorDetailsIntro';
import IndicatorForecast from './IndicatorDetailQuestions/IndicatorForecast';
import IndicatorProgramEnd from './IndicatorDetailQuestions/IndicatorProgramEnd';
import IndicatorTimePeriod from './IndicatorDetailQuestions/IndicatorTimePeriod';
import IndicatorMeasurement from './IndicatorDetailQuestions/IndicatorMeasurement';
import IndicatorFileUpload from './IndicatorDetailQuestions/IndicatorFileUpload';
import IndicatorCompleted from './IndicatorDetailQuestions/IndicatorCompleted';
import {
  getPreviousAndNextSectionDetails,
  IndicatorDetails,
  IndicatorDetailSectionProps,
  IndicatorDetailsRoutes,
  mapIndicatorsById,
} from './utils';
import { SectionIndicatorDetailsProps } from './SectionIndicatorDetails';
import IndicatorDosage from './IndicatorDetailQuestions/IndicatorDosage';
import { ReportPeriod } from '../SectionPeriod/utils';
import { requestInit, RequestState } from 'shared/auth/hooks/useRequestState';
import { SaveReportIndicatorsResponse } from 'shared/providers/IndicatorProvider/hooks';

interface IndicatorRoutesProps
  extends Omit<
    IndicatorDetailSectionProps,
    'onNext' | 'requestState' | 'indicator'
  > {
  beneficiaryTerm?: string;
  programName?: string;
  intermediaries?: string;
  serviceGoal?: string;
  serviceType?: string;
  allIndicators: ReturnType<typeof mapIndicatorsById>;
  reportPeriod: ReportPeriod;
  isReportPassed: boolean;
  reportId: number | string;
  route: IndicatorDetailsRoutes;
  saveRequestState: RequestState<unknown>;
  goToDateSection: () => unknown;
  reloadAnswers: () => unknown;
  onNextReportBuilderSection: () => unknown;
  handleIndicatorSave: (i: IndicatorDetails) => unknown;
  handleNext: (buttonPressed: boolean, answer?: string) => unknown;
  setRoute: React.Dispatch<React.SetStateAction<IndicatorDetailsRoutes>>;
  onPrevious: SectionIndicatorDetailsProps['onPreviousSection'];
  setSaveRequestState: React.Dispatch<
    React.SetStateAction<RequestState<SaveReportIndicatorsResponse>>
  >;
}

const IndicatorRoutes: React.FC<IndicatorRoutesProps> = ({
  route,
  saving,
  indicators,
  guidanceText,
  isReportPassed,
  beneficiaryTerm,
  programName,
  intermediaries,
  serviceGoal,
  serviceType,
  allIndicators,
  reportPeriod,
  saveRequestState,
  reloadAnswers,
  setIndicators,
  handleIndicatorSave,
  handleNext,
  goToDateSection,
  setSaveRequestState,
  selectedSectionKey,
  setSelectedSectionKey,
  selectedIndex,
  setSelectedIndex,
  setRoute,
  onNextReportBuilderSection,
  onPrevious,
  reportId,
}) => {
  const [state, setState] = useState<{
    state: IndicatorDetailSectionProps['requestState'];
    nextRoute: IndicatorDetailsRoutes;
  }>({ state: 'init', nextRoute: 'forecast' });

  useEffect(() => {
    switch (saveRequestState.type) {
      case 'REQUEST_START':
        setState(s => ({ ...s, state: 'saving' }));
        break;
      case 'REQUEST_SUCCESS':
        setState(s => ({ ...s, state: 'saved' }));
        setSaveRequestState(requestInit());
        window.setTimeout(
          () => {
            setRoute(state.nextRoute);
            setState(s => ({ ...s, state: 'init' }));
          },
          state.nextRoute === 'completed' ? 2500 : 1000,
        );
        break;
      case 'REQUEST_ERROR':
        setState(s => ({ ...s, state: 'error' }));
        break;
      // case 'REQUEST_INIT':
      //   setState((s) => ({ ...s, state: 'init' }));
      //   break;
      default:
        break;
    }
  }, [saveRequestState]);

  const handleRoute = useCallback(
    (route: IndicatorDetailsRoutes, isBack?: boolean) => {
      return (answer?: Partial<IndicatorDetails>) => {
        const { nextIndicator } = getPreviousAndNextSectionDetails(
          indicators,
          selectedSectionKey,
          selectedIndex,
        );
        if (nextIndicator && route === 'completed') {
          setSelectedIndex(s => s + 1);
          setRoute('forecast');
          return;
        }
        if (answer && answer.happensAfterProgramYear === 'Yes') {
          route = 'timePeriod';
        }
        if (!isBack && route !== 'forecast' && route !== 'intro') {
          setState({ state: 'saving', nextRoute: route });
        } else {
          setRoute(route);
        }
      };
    },
    [setRoute, setState, indicators, selectedSectionKey, selectedIndex],
  );

  const handlePreviousSection = useCallback(() => {
    const {
      previousSectionKey,
      previousIndicator,
      lastIndexOfPreviousSection,
    } = getPreviousAndNextSectionDetails(
      indicators,
      selectedSectionKey,
      selectedIndex,
    );
    if (previousIndicator) {
      setSelectedIndex(s => s - 1);
      setRoute('file');
      return;
    }
    if (previousSectionKey) {
      setSelectedSectionKey(previousSectionKey);
      setSelectedIndex(lastIndexOfPreviousSection || 0);
      setRoute('completed');
      return;
    }
    setRoute('intro');
  }, [
    onPrevious,
    setRoute,
    setSelectedIndex,
    setSelectedSectionKey,
    selectedSectionKey,
    indicators,
    selectedSectionKey,
    selectedIndex,
  ]);

  const indicatorDetails = indicators[selectedSectionKey][selectedIndex] || {};
  const indicator = allIndicators[indicatorDetails.indicatorId];
  const shouldShowDosage =
    selectedSectionKey === 'reach' && indicatorDetails.indicatorId === 10032;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).getIndicatorId = () => indicatorDetails.indicatorId;

  switch (route) {
    case 'completed':
      return (
        <IndicatorCompleted
          saving={saving}
          requestState={state.state}
          reportId={reportId}
          beneficiaryTerm={beneficiaryTerm}
          programName={programName}
          intermediaries={intermediaries}
          serviceGoal={serviceGoal}
          serviceType={serviceType}
          allIndicators={allIndicators}
          reportPeriod={reportPeriod}
          guidanceText={guidanceText}
          setRoute={setRoute}
          reloadAnswers={reloadAnswers}
          handleNext={handleNext}
          onNextReportBuilderSection={onNextReportBuilderSection}
          indicator={indicator}
          indicators={indicators}
          isReportPassed={isReportPassed}
          setIndicators={setIndicators}
          handleIndicatorSave={handleIndicatorSave}
          saveRequestState={saveRequestState}
          selectedSectionKey={selectedSectionKey}
          setSelectedSectionKey={setSelectedSectionKey}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
          onPrevious={handleRoute('file', true)}
        />
      );
    case 'file':
      return (
        <IndicatorFileUpload
          saving={saving}
          requestState={state.state}
          reportId={reportId}
          beneficiaryTerm={beneficiaryTerm}
          programName={programName}
          intermediaries={intermediaries}
          serviceGoal={serviceGoal}
          serviceType={serviceType}
          allIndicators={allIndicators}
          indicatorDetails={indicatorDetails}
          indicator={indicator}
          indicators={indicators}
          reportPeriod={reportPeriod}
          guidanceText={guidanceText}
          isReportPassed={isReportPassed}
          setIndicators={setIndicators}
          setRoute={setRoute}
          handleIndicatorSave={handleIndicatorSave}
          selectedSectionKey={selectedSectionKey}
          setSelectedSectionKey={setSelectedSectionKey}
          selectedIndex={selectedIndex}
          saveRequestState={saveRequestState}
          setSelectedIndex={setSelectedIndex}
          onNext={handleRoute('completed')}
          onPrevious={handleRoute('measurement', true)}
        />
      );
    case 'measurement':
      return (
        <IndicatorMeasurement
          saving={saving}
          requestState={state.state}
          reportId={reportId}
          beneficiaryTerm={beneficiaryTerm}
          programName={programName}
          intermediaries={intermediaries}
          serviceGoal={serviceGoal}
          serviceType={serviceType}
          allIndicators={allIndicators}
          indicatorDetails={indicatorDetails}
          indicator={indicator}
          indicators={indicators}
          reportPeriod={reportPeriod}
          guidanceText={guidanceText}
          isReportPassed={isReportPassed}
          setRoute={setRoute}
          setIndicators={setIndicators}
          handleIndicatorSave={handleIndicatorSave}
          selectedSectionKey={selectedSectionKey}
          setSelectedSectionKey={setSelectedSectionKey}
          selectedIndex={selectedIndex}
          saveRequestState={saveRequestState}
          setSelectedIndex={setSelectedIndex}
          onNext={handleRoute('file')}
          onPrevious={handleRoute(
            selectedSectionKey === 'succeed'
              ? 'timePeriod'
              : // : shouldShowDosage
                // ? 'dosage'
                'forecast',

            true,
          )}
        />
      );
    case 'timePeriod':
      return (
        <IndicatorTimePeriod
          saving={saving}
          requestState={state.state}
          reportId={reportId}
          beneficiaryTerm={beneficiaryTerm}
          programName={programName}
          intermediaries={intermediaries}
          serviceGoal={serviceGoal}
          serviceType={serviceType}
          allIndicators={allIndicators}
          indicatorDetails={indicatorDetails}
          indicator={indicator}
          indicators={indicators}
          reportPeriod={reportPeriod}
          guidanceText={guidanceText}
          isReportPassed={isReportPassed}
          setRoute={setRoute}
          setIndicators={setIndicators}
          handleIndicatorSave={handleIndicatorSave}
          goToDateSection={goToDateSection}
          selectedSectionKey={selectedSectionKey}
          setSelectedSectionKey={setSelectedSectionKey}
          selectedIndex={selectedIndex}
          saveRequestState={saveRequestState}
          setSelectedIndex={setSelectedIndex}
          onNext={handleRoute('measurement')}
          onPrevious={handleRoute('forecast', true)}
        />
      );
    case 'dosage':
      return (
        <IndicatorDosage
          saving={saving}
          requestState={state.state}
          reportId={reportId}
          beneficiaryTerm={beneficiaryTerm}
          programName={programName}
          intermediaries={intermediaries}
          serviceGoal={serviceGoal}
          serviceType={serviceType}
          allIndicators={allIndicators}
          indicatorDetails={indicatorDetails}
          isReportPassed={isReportPassed}
          indicator={indicator}
          indicators={indicators}
          reportPeriod={reportPeriod}
          guidanceText={guidanceText}
          setRoute={setRoute}
          setIndicators={setIndicators}
          handleIndicatorSave={handleIndicatorSave}
          selectedSectionKey={selectedSectionKey}
          setSelectedSectionKey={setSelectedSectionKey}
          selectedIndex={selectedIndex}
          saveRequestState={saveRequestState}
          setSelectedIndex={setSelectedIndex}
          onNext={handleRoute('measurement')}
          onPrevious={handleRoute('forecast', true)}
        />
      );
    case 'happensAfterProgramYear':
      return (
        <IndicatorProgramEnd
          saving={saving}
          requestState={state.state}
          reportId={reportId}
          beneficiaryTerm={beneficiaryTerm}
          programName={programName}
          intermediaries={intermediaries}
          serviceGoal={serviceGoal}
          serviceType={serviceType}
          allIndicators={allIndicators}
          indicatorDetails={indicatorDetails}
          isReportPassed={isReportPassed}
          indicator={indicator}
          indicators={indicators}
          reportPeriod={reportPeriod}
          guidanceText={guidanceText}
          setRoute={setRoute}
          setIndicators={setIndicators}
          handleIndicatorSave={handleIndicatorSave}
          selectedSectionKey={selectedSectionKey}
          setSelectedSectionKey={setSelectedSectionKey}
          selectedIndex={selectedIndex}
          saveRequestState={saveRequestState}
          setSelectedIndex={setSelectedIndex}
          onNext={handleRoute('measurement')}
          onPrevious={handleRoute('forecast', true)}
        />
      );
    case 'forecast':
      return (
        <IndicatorForecast
          saving={saving}
          requestState={state.state}
          reportId={reportId}
          beneficiaryTerm={beneficiaryTerm}
          programName={programName}
          intermediaries={intermediaries}
          serviceGoal={serviceGoal}
          serviceType={serviceType}
          allIndicators={allIndicators}
          indicatorDetails={indicatorDetails}
          indicator={indicator}
          indicators={indicators}
          reportPeriod={reportPeriod}
          guidanceText={guidanceText}
          isReportPassed={isReportPassed}
          setRoute={setRoute}
          setIndicators={setIndicators}
          handleIndicatorSave={handleIndicatorSave}
          selectedSectionKey={selectedSectionKey}
          setSelectedSectionKey={setSelectedSectionKey}
          selectedIndex={selectedIndex}
          saveRequestState={saveRequestState}
          setSelectedIndex={setSelectedIndex}
          onNext={handleRoute(
            selectedSectionKey === 'succeed'
              ? 'timePeriod'
              : // : shouldShowDosage
                // ? 'dosage'
                'measurement',
          )}
          onPrevious={handlePreviousSection}
        />
      );
    case 'intro':
    default:
      return (
        <IndicatorDetailsIntro
          saving={saving}
          requestState={state.state}
          reportId={reportId}
          allIndicators={allIndicators}
          beneficiaryTerm={beneficiaryTerm}
          programName={programName}
          intermediaries={intermediaries}
          serviceGoal={serviceGoal}
          serviceType={serviceType}
          indicator={indicator}
          indicators={indicators}
          reportPeriod={reportPeriod}
          guidanceText={guidanceText}
          setRoute={setRoute}
          setIndicators={setIndicators}
          handleIndicatorSave={handleIndicatorSave}
          selectedSectionKey={selectedSectionKey}
          setSelectedSectionKey={setSelectedSectionKey}
          selectedIndex={selectedIndex}
          saveRequestState={saveRequestState}
          isReportPassed={isReportPassed}
          setSelectedIndex={setSelectedIndex}
          onNext={handleRoute('forecast')}
          onPrevious={onPrevious}
        />
      );
  }
};

export default IndicatorRoutes;
